/**
 * THIS IS DEPRECATED PLEASE DONT" USE IT
 */

/** Centralised app routes — import from here rather than hard-coding paths. */

/**
 * Who a screen is for. `owner` and `admin` are `users` rows; `practitioner` is a
 * `practitioner_accounts` row. They are separate tables with separate logins — see `AuthSurface`.
 */
export type Role = "owner" | "staff" | "practitioner";

/** Roles allowed through a route whose `access.role` is `admin` — owners are staff too. */
export const isStaff = (role: string): role is "owner" | "staff" => role === "owner" || role === "staff";

/**
 * Which of the two auth stacks a screen talks to. Practitioners are their own entity, not a role
 * on `users`, and carry their own session cookie. Every auth-aware call has to pick one — there is
 * no shared "current user".
 */
export type AuthSurface = "practitioner" | "staff";

export const isAuthSurface = (value: string | null): value is AuthSurface =>
  value === "practitioner" || value === "staff";

export const surfaceForRole = (role: Role): AuthSurface => (isStaff(role) ? "staff" : "practitioner");

export type RouteAccess =
  | { type: "public" }
  /** Guest-only pages (sign-in, sign-up, ...) — signed-in users get bounced to their role home. */
  | { type: "auth" }
  | { type: "private"; role: Role };

export interface CmwRoute {
  url: string;
  access: RouteAccess;
}

export const routes = {
  home: { url: "/", access: { type: "public" } },
  signIn: { url: "/auth/sign-in", access: { type: "auth" } },
  signUp: { url: "/auth/sign-up", access: { type: "auth" } },
  forgotPassword: { url: "/auth/forgot-password", access: { type: "auth" } },
  /** Takes `?state=sent|required` to pick the variant, and `?email=` for the address to show. */
  verifyEmail: { url: "/auth/verify-email", access: { type: "auth" } },
  /** Where the practitioner migration email lands, with `?email=`. That URL is baked into sent
   *  emails — do not change it. Practitioner-only; there is no admin twin. */
  signInWithCode: { url: "/auth/sign-in-with-code", access: { type: "auth" } },
  /** Google's redirect target — a route handler, not a page. Must stay outside `/api` so the DO
   * ingress rule that proxies `/api/*` to the backend doesn't swallow it. `public` so `proxy.ts`
   * passes it straight through; the handler owns its own auth/redirect logic for all three flows
   * it serves (sign-in, invitation acceptance, linking). */
  authCallback: { url: "/auth/callback", access: { type: "public" } },
  /** Where a Google round trip starts — a route handler, not a page. Outside `/api` for the same
   * reason as `authCallback`: the DO ingress rule proxies `/api/*` straight to the backend, which
   * has no such endpoint. `public` so `proxy.ts` lets it through; it only records the surface and
   * forwards to the backend controller that issues the state. */
  authGoogleStart: { url: "/auth/google/start", access: { type: "public" } },
  dashboard: { url: "/dashboard", access: { type: "private", role: "practitioner" } },
  dashboardEnquiries: { url: "/dashboard/enquiries", access: { type: "private", role: "practitioner" } },
  dashboardProfile: { url: "/dashboard/profile", access: { type: "private", role: "practitioner" } },
  /** Section root — the page at this URL only redirects to `adminDashboard`. */
  admin: { url: "/admin", access: { type: "private", role: "staff" } },
  adminSignIn: { url: "/admin/auth/sign-in", access: { type: "auth" } },
  adminForgotPassword: { url: "/admin/auth/forgot-password", access: { type: "auth" } },
  /** Where the invitation email lands, with `?token=…`. That URL is baked into sent emails — do
   * not change it. Guest-only: accepting mints a fresh session. */
  adminAcceptInvitation: { url: "/admin/auth/accept-invitation", access: { type: "auth" } },
  /** Everything signed-in lives under here, so `admin/dashboard/layout.tsx` can own the sidebar
   * without also wrapping `adminSignIn`. */
  adminDashboard: { url: "/admin/dashboard", access: { type: "private", role: "staff" } },
  adminEnquiries: { url: "/admin/dashboard/enquiries", access: { type: "private", role: "staff" } },
  adminProfile: { url: "/admin/dashboard/profile", access: { type: "private", role: "staff" } },
  /** Everything practitioner-shaped an admin looks at, in one section. Section root, and the
   * tabbed overview itself (Approved / Pending Review / License Expired / Hidden from Directory);
   * a single practitioner's profile (`/admin/dashboard/practitioners/:id`) inherits this via
   * `getRouteByUrl`'s prefix match. */
  adminPractitioners: { url: "/admin/dashboard/practitioners", access: { type: "private", role: "staff" } },
  /** One application, read end to end. Sits beside the queues rather than inside the Pending
   * Review tab: the same screen serves an approved or declined application, which no longer
   * belongs there. */
  adminPractitionersApplication: {
    url: "/admin/dashboard/practitioners/application",
    access: { type: "private", role: "staff" },
  },
  adminPractitionersLicenseReview: {
    url: "/admin/dashboard/practitioners/license-review",
    access: { type: "private", role: "staff" },
  },
  /** Section root, and the Active list itself — `adminUsersInvited` is the one sub-page. */
  adminUsers: { url: "/admin/dashboard/users", access: { type: "private", role: "staff" } },
  adminUsersInvited: { url: "/admin/dashboard/users/invited", access: { type: "private", role: "staff" } },
  adminTaxonomy: { url: "/admin/dashboard/taxonomy", access: { type: "private", role: "staff" } },
  policyRelease: {
    url: "https://cmw.org.au/wp-content/uploads/2026/05/Response-to-the-Mental-Health-and-Wellbeing-Commission-Issues-Paper-2-March-2026.pdf",
    access: { type: "public" },
  },
  privacyPolicy: { url: "https://cmw.org.au/privacy-policy/", access: { type: "public" } },
  futurereadyx: { url: "https://futurereadyx.agency/", access: { type: "public" } },
  supportAgreement: { url: "https://cmw.org.au/support-services-agreement/", access: { type: "public" } },
  cmwHome: { url: "https://cmw.org.au/", access: { type: "public" } },
  urgentHelp: { url: "https://cmw.org.au/seek-help/", access: { type: "public" } },
  donation: { url: "https://cmw.org.au/donate/", access: { type: "public" } },
  savedPractitioners: { url: "/saved", access: { type: "public" } },
  /** Landed on after a successful "Contact Me" submission. Takes `?name=` for the practitioner
   * the inquiry was sent to. */
  inquirySent: { url: "/inquiry-sent", access: { type: "public" } },
  /** Section root — every profile (`/practitioner/:id`) inherits this via `getRouteByUrl`'s
   * prefix match. */
  practitionerProfile: { url: "/practitioner", access: { type: "public" } },
  components: { url: "/components", access: { type: "public" } },
  /** Practitioner onboarding, not a public page. Section root — every step (`/apply/details`,
   * `/apply/review`, …) inherits this via `getRouteByUrl`'s prefix match. A verified practitioner
   * is confined here until their application is accepted; see the gate in `proxy.ts`. */
  apply: { url: "/apply", access: { type: "private", role: "practitioner" } },
} as const satisfies Record<string, CmwRoute>;

/** Where the gate sends an applicant — `/apply` is a section root with no page of its own. */
export const applyStart = `${routes.apply.url}/details`;

/** The review screen for one application. */
export const adminApplicationUrl = (applicationId: string) =>
  `${routes.adminPractitionersApplication.url}/${encodeURIComponent(applicationId)}`;

/** The admin's read of one live practitioner — the public profile plus their licence standing. */
export const adminPractitionerUrl = (practitionerId: string) =>
  `${routes.adminPractitioners.url}/${encodeURIComponent(practitionerId)}`;

export const adminPractitionerLicenseUrl = (practitionerId: string) =>
  `${adminPractitionerUrl(practitionerId)}/license`;

/** Where a signed-in user of a given role lands by default. */
export const roleHome: Record<Role, string> = {
  /** Owners share the admin dashboard — their permissions diverge inside it, not at the door. */
  owner: routes.adminDashboard.url,
  staff: routes.adminDashboard.url,
  practitioner: routes.dashboard.url,
};

/**
 * The sign-in and forgot-password pair for a role's surface. Staff are onboarded through `/admin`
 * and never see the practitioner pages, so the two surfaces carry their own copy of each — this is
 * what lets one form serve both.
 */
/** Where a role manages its own account — the two surfaces carry their own copy of the screen. */
export const profileFor = (role: Role) =>
  isStaff(role) ? routes.adminProfile.url : routes.dashboardProfile.url;

export const authPages = (role: Role) =>
  isStaff(role)
    ? { signIn: routes.adminSignIn.url, forgotPassword: routes.adminForgotPassword.url }
    : { signIn: routes.signIn.url, forgotPassword: routes.forgotPassword.url };

/**
 * Which stack owns a URL. Everything under `/admin` is staff; everything else that needs a session
 * is a practitioner screen. This is what lets `proxy.ts` pick the right `@me` — the two surfaces
 * have separate sessions, so "signed in" is only ever a question about one of them.
 */
export const surfaceForUrl = (url: string): AuthSurface =>
  url === routes.admin.url || url.startsWith(`${routes.admin.url}/`) ? "staff" : "practitioner";

/** Where a guest gets sent when a private route turns them away. */
export const signInFor = (surface: AuthSurface) =>
  surface === "staff" ? routes.adminSignIn.url : routes.signIn.url;

export const isRole = (value: string): value is Role =>
  value === "owner" || value === "staff" || value === "practitioner";

/**
 * Matches a pathname to its route, preferring the most specific (longest
 * `url`) match so nested paths (e.g. `/admin/settings`) inherit their
 * section's access rules without needing their own entry.
 */
export const getRouteByUrl = (pathname: string): CmwRoute | undefined => {
  const matches = Object.values(routes).filter(
    (route) => pathname === route.url || pathname.startsWith(`${route.url}/`),
  );

  return matches.sort((a, b) => b.url.length - a.url.length)[0];
};

/**
 * Where a Google round trip starts, hit via full-page navigation. These cannot be `fetch`ed: the
 * backend answers with a redirect to Google's consent screen, and it sets the state cookie the
 * callback checks — both need the browser to follow the navigation itself.
 *
 * The hop goes through our own `authGoogleStart` rather than straight at the backend because
 * Google is registered against one redirect URI: the callback has no way to tell a practitioner
 * sign-in from a staff one unless the surface is recorded on the way out.
 */
const googleStart = (surface: AuthSurface, params: Record<string, string> = {}) => {
  const search = new URLSearchParams({ surface, ...params });

  return `${routes.authGoogleStart.url}?${search}`;
};

export const authApi = {
  google: (surface: AuthSurface) => googleStart(surface),
  /** Adds Google to the account already signed in, rather than signing a user in. */
  googleLink: (surface: AuthSurface) => googleStart(surface, { intent: "link" }),
  /** Invitations are staff-only — practitioners sign themselves up. */
  googleInvitation: (token: string) => googleStart("staff", { invitation: token }),
} as const;
